import { ComponentPropsWithoutRef } from 'react';
import clsx from 'clsx';

import { Link } from '@northladder/i18n';

import { ButtonLoader } from './forms';

type TButtonType = ComponentPropsWithoutRef<'button'>['type'];

/**
 * Properties for the ButtonAlt component.
 */
type IButtonAltProps = (
  | ComponentPropsWithoutRef<typeof Link>
  | (ComponentPropsWithoutRef<'button'> & { href?: undefined })
) & {
  /** Whether to invert the colors for dark backgrounds. */
  invert?: boolean;
  /** Whether the link points to an external resource. */
  isExternalLink?: boolean;
  /** Whether the button is currently in a loading state. */
  isLoading?: boolean;
  /** Whether the button is enabled. */
  isEnabled?: boolean;
  /** The type of the button element. */
  type?: TButtonType;
};

/**
 * An alternative button component that can also act as a link.
 */
export function ButtonAlt({
  children,
  className,
  invert = false,
  isEnabled = true,
  isLoading = false,
  type = 'button',
  ...props
}: IButtonAltProps) {
  const newClassName = clsx(
    `
      group isolate items-center justify-center rounded-full px-4 py-2 text-sm
      font-medium ring-1 transition duration-800 hover:scale-[1.04]
      hover:ring-slate-300 focus:outline-none focus-visible:outline-slate-200/50
      focus-visible:ring-slate-200 active:bg-slate-100 active:text-gray-600
    `,
    invert
      ? 'bg-white text-gray-950 ring-slate-200 hover:bg-green-50 hover:text-slate-900'
      : 'bg-gray-950 text-white ring-green-950  hover:bg-gray-800 hover:text-green-100',
    className
  );

  if (typeof props.href === 'undefined') {
    return (
      // eslint-disable-next-line react/button-has-type
      <button className={newClassName} type={type} {...props}>
        {isLoading ? (
          <ButtonLoader className="size-4 me-2" isEnabled={!isEnabled} />
        ) : null}
        {children}
      </button>
    );
  }

  return (
    <Link className={newClassName} {...props}>
      {isLoading ? (
        <ButtonLoader className="size-4 me-2" isEnabled={isEnabled} />
      ) : null}
      {children}
    </Link>
  );
}
